"use client";
import React from "react";
import { Controller } from "react-hook-form";
import RatingImg from "~/icons/rating.png";
import "./index.css";
import { ALL_COUNTRIES } from "@/context/countries";
import CountryDropdownGlobalReferral from "@/components/atoms/FormComponents/CustomDropdownGlobalReferral/CustomDropDownReferral";
import { useReferralFormController } from "../ReferralForm/controller/useReferralFormController";
import type { ReferralProgramOption } from "../ReferralForm/model/referralForm.types";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";
import { onInpSafeButtonClick } from "@/helpers/inpSafeForm";

interface ReferralFormNewProps {
  programs?: ReferralProgramOption[];
}

const ReferralFormNew = ({
  programs: programsProp,
}: ReferralFormNewProps = {}) => {
  const {
    step,
    control,
    programs,
    errors,
    showOtpBox,
    isSubmitting,
    isValidationDisabled,
    isValidationUsed,
    timerLabel,
    resendEnabled,
    stickyCtaRef,
    handleChange,
    handleProgramChange,
    handleCountryCodeChange,
    updateCountryCode,
    fetchOTP,
    resendOTP,
    handleOtpChange,
    handleOtpKeyDown,
    handlePaste,
    otpRef,
    handleFormSubmit,
    handleReferMore,
  } = useReferralFormController({
    programs: programsProp,
    enableStickyCta: true,
    strictFullName: true,
    enableReferMoreReset: true,
  });

  const fieldBase =
    "w-full rounded-xl border bg-[#F7F8FA] px-4 py-2 text-base sm:text-[15px] text-[#16285C] placeholder-gray-400 outline-none transition-colors focus:border-[#FFC907] focus:ring-2 focus:ring-[#FFC907]/30 disabled:opacity-60";
  const borderCls = (err?: string) => (err ? "border-red-500" : "border-gray-200");
  const errorCls =
    "text-[#e60000] text-xs font-medium leading-4 mt-1 tracking-[0.015rem]";

  return (
    <div
      ref={stickyCtaRef}
      id="referral-form-card"
      className={`bg-white rounded-[24px] p-4 md:p-8 border border-gray-100 shadow-[0_20px_60px_rgba(15,23,42,0.12)] right`}
    >
      {step == 1 && (
        <>
          <h3 className="text-[#16285C] font-bold text-xl sm:text-md md:text-[26px]  mb-3 sm:mb-3">
            Enter Your Friend&apos;s Details
          </h3>
          <InpSafeForm className="form_handle" onSubmit={handleFormSubmit}>
            <div className="space-y-2.5 sm:space-y-4">
              <div style={{ position: "relative" }}>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Full Name*"
                      value={field.value ?? ""}
                      onChange={handleChange}
                      onBlur={field.onBlur}
                      className={`${fieldBase} ${borderCls(errors.fullName)}`}
                      disabled={isSubmitting}
                    />
                  )}
                />
                {errors.fullName && (
                  <p className={errorCls}>{errors.fullName}</p>
                )}
              </div>
              <div>
                <div className="flex w-full gap-2">
                  <Controller
                    name="countryCode"
                    control={control}
                    render={({ field }) => (
                      <CountryDropdownGlobalReferral
                        onChangeFunc={(value: string) =>
                          handleCountryCodeChange("countryCode", value)
                        }
                        countries={ALL_COUNTRIES}
                        countryCode={field.value}
                        updateCountryCode={updateCountryCode}
                        additionalStyle={""}
                        isDark={false}
                        isSpecificPage={false}
                        isRefferalPage={true}
                      />
                    )}
                  />
                  <Controller
                    name="mobile"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="text"
                        name="mobile"
                        placeholder="Mobile No.*"
                        value={field.value ?? ""}
                        onChange={handleChange}
                        onBlur={field.onBlur}
                        className={`${fieldBase} ${borderCls(errors.mobile)} lining-nums`}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </div>
                {(errors.countryCode || errors.mobile) && (
                  <p className={errorCls}>{errors.countryCode || errors.mobile}</p>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address*"
                      value={field.value ?? ""}
                      onChange={handleChange}
                      onBlur={field.onBlur}
                      className={`${fieldBase} ${borderCls(errors.email)}`}
                      disabled={isSubmitting}
                    />
                  )}
                />
                {errors.email && (
                  <p className={errorCls}>{errors.email}</p>
                )}
              </div>

              <div style={{ position: "relative" }}>
                <Controller
                  name="program"
                  control={control}
                  render={({ field }) => (
                    <select
                      className={`${fieldBase} appearance-none ${borderCls(errors.program)}`}
                      name="referredCourse"
                      value={field.value?.index || 0}
                      disabled={isSubmitting}
                      onChange={(e) => {
                        handleProgramChange(parseInt(e.target.value));
                      }}
                      onBlur={field.onBlur}
                    >
                      <option value={0}>Select course*</option>
                      {programs.map((program) => (
                        <option key={program.index} value={program.index}>
                          {program.ProgramName}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.program && (
                  <p className={errorCls}>{errors.program}</p>
                )}
              </div>

              <div style={{ position: "relative" }}>
                <Controller
                  name="counsellorUsername"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="text"
                      name="counsellorUsername"
                      placeholder="Counsellor Username (optional)"
                      value={field.value ?? ""}
                      onChange={handleChange}
                      onBlur={field.onBlur}
                      className={`${fieldBase} ${borderCls(errors.counsellorUsername)}`}
                      disabled={isSubmitting}
                    />
                  )}
                />
                {errors.counsellorUsername && (
                  <p className={errorCls}>
                    {errors.counsellorUsername}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4" style={{ position: "relative" }}>
              <div>
                <div className="space-y-3">
                  {!showOtpBox ? (
                    <>
                      <div className="flex items-stretch">
                        <Controller
                          name="verificationEmailOrMobile"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              name="verificationEmailOrMobile"
                              placeholder="Your Amity Online E-mail ID"
                              value={field.value ?? ""}
                              onChange={handleChange}
                              onBlur={field.onBlur}
                              className={`flex-1 min-w-0 rounded-l-xl border bg-[#F7F8FA] px-4 py-2 sm:py-2 text-base sm:text-[15px] text-[#16285C] placeholder-gray-400 outline-none transition-colors focus:border-[#FFC907] ${
                                errors.verificationEmailOrMobile
                                  ? "border-red-500"
                                  : "border-gray-200"
                              }`}
                              disabled={isValidationUsed || isSubmitting}
                              id="verify_detail"
                            />
                          )}
                        />
                        <button
                          type="button"
                          className={`rounded-r-xl bg-[#16285C] px-5 text-sm font-bold text-white transition-colors hover:bg-[#0f1d44] ${
                            isValidationDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                          onClick={fetchOTP}
                          disabled={isValidationDisabled}
                        >
                          {isValidationUsed ? "Validating..." : "Validate"}
                        </button>
                      </div>
                      {errors.verificationEmailOrMobile && (
                        <p className={`${errorCls} !mt-1`}>
                          {errors.verificationEmailOrMobile}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-[#16285C] font-bold text-base underline mb-3">
                          Enter OTP sent to your details
                        </p>
                      </div>
                      <Controller
                        name="otp"
                        control={control}
                        render={({ field }) => (
                          <div className="grid grid-cols-6 gap-2 md:gap-3">
                            {(field.value ?? []).map((digit, index) => (
                              <input
                                id={`otp-input-${index}`}
                                key={index}
                                type="tel"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                maxLength={1}
                                value={digit}
                                ref={(el) => otpRef(el, index)}
                                onChange={(e) =>
                                  handleOtpChange(index, e.target.value)
                                }
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                className={`h-10 sm:h-12 md:h-14 w-full rounded-xl border border-gray-200 bg-[#F7F8FA] text-center text-xl font-semibold text-[#16285C] outline-none transition-colors focus:border-[#FFC907] focus:ring-2 focus:ring-[#FFC907]/30`}
                                onPaste={(e) => handlePaste(e, index)}
                              />
                            ))}
                          </div>
                        )}
                      />
                      {errors.otp && (
                        <p className={`${errorCls} mt-2`}>
                          {errors.otp}
                        </p>
                      )}
                      {resendEnabled ? (
                        <a
                          href="#"
                          onClick={resendOTP}
                          className="font-bold text-[#16285C] underline text-sm decoration-1"
                        >
                          Resend OTP
                        </a>
                      ) : (
                        <p className="text-sm text-gray-500">
                          <span className="font-bold text-[#16285C]">
                            Resend OTP
                          </span>{" "}
                          in {timerLabel}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                name="submit_btn"
                onClick={onInpSafeButtonClick}
                className={`w-full rounded-xl py-2.5 sm:py-2 text-base font-bold transition-colors ${
                  isSubmitting
                    ? "bg-[#FFC907] text-gray-500 cursor-not-allowed"
                    : "bg-[#FFC907] text-[#16285C] hover:bg-[#f5c200] shadow-[0_10px_24px_rgba(255,201,7,0.45)] cursor-pointer"
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="w-6 h-6 border-4 m-auto border-[#16285C] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span>Continue</span>
                )}
              </button>
            </div>
          </InpSafeForm>
        </>
      )}
      {step == 2 && (
        <div className="flex flex-col items-center text-center p-6">
          <div className="mb-6">
            <img src={RatingImg.src} alt="Rating" className="w-24" />
          </div>
          <h5 className="text-[#1A325D] font-serif text-2xl">
            Thank you for sharing details.
          </h5>
          <p className="text-gray-700 mt-4">
            We will inform you if your reference gets admission in Amity Online
            and also share your reward.
          </p>
          <button
            type="button"
            onClick={handleReferMore}
            className="mt-6 px-3 py-1 border border-[#1a325d] text-[#1a325d] rounded-3xl transition uppercase font-semibold "
          >
            Refer More!
          </button>
        </div>
      )}
    </div>
  );
};

export default ReferralFormNew;
